var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import setEmail from 'root/src/client/logic/auth/thunks/setEmail';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
const emailSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});
export const EmailSettingsPanel = ({ canChange = false }) => {
    var _a;
    const dispatch = useAppDispatch();
    const { authentication } = useAuthentication();
    const currentEmail = authentication === null || authentication === void 0 ? void 0 : authentication.email;
    const [editing, setEditing] = useState(false);
    const [failed, setFailed] = useState(false);
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(emailSchema),
    });
    const closeEditing = () => {
        setEditing(false);
        setFailed(false);
        reset();
    };
    const onSubmit = handleSubmit(({ email }) => __awaiter(void 0, void 0, void 0, function* () {
        setFailed(false);
        try {
            // On success the thunk sends a verification email and redirects to the verify page.
            yield dispatch(setEmail({ email }));
        }
        catch (e) {
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
            const message = e instanceof Error ? e.message : 'Please try again.';
            toast.error('Could not update email', { description: message });
        }
    }), () => {
        toast.error('Invalid email', { description: 'Enter a valid email address.' });
    });
    return (<div>
			<h3 className="mb-1 text-sm font-semibold">Email</h3>
			<p className="mb-4 text-xs text-muted-foreground">The email address associated with your account.</p>

			<div className="flex items-center justify-between gap-3 rounded-lg border border-solid border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
				<TooltipProvider delayDuration={200}>
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="min-w-0 flex-1 truncate text-sm">
								{currentEmail || <span className="text-muted-foreground">No email on file</span>}
							</span>
						</TooltipTrigger>
						{currentEmail && <TooltipContent side="top">{currentEmail}</TooltipContent>}
					</Tooltip>
				</TooltipProvider>
				{canChange && !editing && (<Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)} className="h-8 flex-shrink-0 rounded-lg text-[11px]">
						Change email
					</Button>)}
			</div>

			<AnimatePresence initial={false}>
				{canChange && editing && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }} className="overflow-hidden">
						<form onSubmit={onSubmit} className="space-y-3 pt-4">
							<p className="text-xs text-muted-foreground">We will send a confirmation link to verify your new email.</p>
							<Field label="New email" htmlFor="new-email" error={(_a = errors.email) === null || _a === void 0 ? void 0 : _a.message}>
								<Input id="new-email" type="email" placeholder="you@example.com" autoComplete="email" autoFocus error={!!errors.email} {...register('email')}/>
							</Field>
							<div className="flex justify-end gap-2">
								<Button type="button" variant="ghost" size="sm" onClick={closeEditing} className="h-8 rounded-lg text-[11px]">
									Cancel
								</Button>
								<Button type="submit" size="sm" loading={isSubmitting} error={failed} className="h-8 min-w-[110px] rounded-lg text-[11px]">
									Update email
								</Button>
							</div>
						</form>
					</motion.div>)}
			</AnimatePresence>
		</div>);
};
